import { useEffect, useRef, useState } from "react";
import { Building2, Check, ChevronDown } from "lucide-react";
import { cn } from "../../utils/tailwindUtils";

/** Shared trigger styling — matches report / master filter dropdowns (h-10, min-w 200px). */
export const branchFilterTriggerClass = cn(
    "inline-flex items-center justify-between gap-2 min-w-[200px] h-10 px-3 rounded-lg text-sm font-medium",
    "bg-muted/50 border border-[#0BB5AC]/40 text-foreground",
    "hover:bg-[#0BB5AC]/5 hover:border-[#0BB5AC]/60",
    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#0BB5AC]/30"
);

export const branchFilterPanelClass =
    "min-w-[220px] max-h-72 overflow-y-auto rounded-lg border border-[#0BB5AC]/20 bg-background p-1 shadow-lg";

const branchFilterItemClass = (isActive) =>
    cn(
        "w-full flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-left transition-colors",
        "hover:bg-[#0BB5AC]/5 hover:text-[#0BB5AC]",
        isActive ? "bg-[#0BB5AC]/10 text-[#0BB5AC] font-medium" : "text-foreground"
    );

/**
 * Themed branch picker with optional "All branches" row.
 * Used on master pages and anywhere a full-width filter dropdown is needed.
 */
export default function BranchFilterDropdown({
    branches = [],
    value = "all",
    onChange,
    includeAll = true,
    allLabel = "All branches",
    disabled = false,
    className,
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    const options = includeAll
        ? [{ id: "all", name: allLabel, is_main_branch: false }, ...branches]
        : branches;

    const currentKey = value ?? "all";
    const current = options.find((b) => String(b.id) === String(currentKey));
    const currentLabel = current?.name || allLabel;

    useEffect(() => {
        const onDocClick = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    const select = (opt) => {
        onChange?.(String(opt.id));
        setOpen(false);
    };

    return (
        <div className={cn("relative", className)} ref={ref}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && setOpen((o) => !o)}
                className={cn(
                    branchFilterTriggerClass,
                    open && "border-[#0BB5AC]/60 bg-[#0BB5AC]/5 ring-1 ring-[#0BB5AC]/20",
                    disabled && "cursor-not-allowed opacity-70"
                )}
            >
                <Building2 className="h-4 w-4 shrink-0 text-[#0BB5AC]" />
                <span className="flex-1 truncate text-left">{currentLabel}</span>
                <ChevronDown className={cn("h-4 w-4 shrink-0 text-[#0BB5AC] transition-transform", open && "rotate-180")} />
            </button>

            {open && !disabled && (
                <div
                    className={cn(
                        branchFilterPanelClass,
                        "absolute left-0 mt-2 z-50 animate-in fade-in-80 slide-in-from-top-2"
                    )}
                >
                    {options.map((opt) => {
                        const selected = String(opt.id) === String(currentKey);
                        return (
                            <button
                                key={opt.id}
                                type="button"
                                onClick={() => select(opt)}
                                className={branchFilterItemClass(selected)}
                            >
                                <Building2 className="h-4 w-4 shrink-0 opacity-70" />
                                <span className="flex-1 truncate">
                                    {opt.name}
                                    {opt.is_main_branch && (
                                        <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-[#0BB5AC]/15 text-[#0BB5AC] text-[10px] font-semibold">
                                            Main
                                        </span>
                                    )}
                                </span>
                                {selected && <Check className="h-4 w-4 shrink-0 text-[#0BB5AC]" />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/** Ant Design Select props shared by master form dialogs (Block, Floor, etc.). */
export const masterFormSelectProps = {
    size: "large",
    className: "w-full",
    showSearch: true,
    optionFilterProp: "children",
    getPopupContainer: () => (typeof document !== "undefined" ? document.body : undefined),
    dropdownStyle: { zIndex: 3100 },
    styles: { popup: { root: { zIndex: 3100 } } },
};
